import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { EinstufungProgressProvider } from "./context/EinstufungProgressContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import PendingPage from "./pages/PendingPage";
import DashboardPage from "./pages/EinstufungToolPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminKvsPage from "./pages/AdminKvsPage";
import AdminKvStructuredEditor from "./pages/AdminKvStructuredEditor";
import AdminStatisticsPage from "./pages/AdminStatisticsPage";
import AdminHealthPage from "./pages/AdminHealthPage";
import AdminSystemSettingsPage from "./pages/AdminSystemSettingsPage";
import AdminSvCodesPage from "./pages/AdminSvCodesPage";
import AdminCompaniesPage from "./pages/AdminCompaniesPage";
import AdminTestsuitePage from "./pages/AdminTestsuitePage";
import ProfilePage from "./pages/ProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ImpressumPage from "./pages/ImpressumPage";
import HelpPage from "./pages/help/HelpPage";
import MockupStep3Page from "./pages/MockupStep3Page";
import DemoLandingPage from "./pages/DemoLandingPage";
import DemoToolPage from "./pages/DemoToolPage";

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center" data-testid="app-loading">
      <div className="text-center">
        <div className="font-cabinet text-3xl font-black tracking-tight text-ink-900">
          ET<span className="text-brand-400">.</span>
        </div>
        <div className="mt-3 h-0.5 w-32 mx-auto loading-bar" />
        <div className="mt-3 text-xs uppercase tracking-widest text-warm-500">
          Wird geladen…
        </div>
      </div>
    </div>
  );
}

function PublicOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (user) {
    if (user.role === "admin" || user.role === "superadmin") return <Navigate to="/admin/users" replace />;
    if (user.status === "aktiv") return <Navigate to="/dashboard" replace />;
    return <Navigate to="/pending" replace />;
  }
  return children;
}

function ProtectedRoute({ children, requireRole, requireStatus }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (requireRole) {
    // 'admin' requirement accepts both admin and superadmin (admin-level access).
    const isAdminLevel = user.role === "admin" || user.role === "superadmin";
    const ok = requireRole === "admin" ? isAdminLevel : user.role === requireRole;
    if (!ok) {
      if (isAdminLevel) return <Navigate to="/admin/users" replace />;
      return <Navigate to={user.status === "aktiv" ? "/dashboard" : "/pending"} replace />;
    }
  }
  if (requireStatus && user.status !== requireStatus && user.role !== "admin" && user.role !== "superadmin") {
    return <Navigate to="/pending" replace />;
  }
  return children;
}

function PendingRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "superadmin") return <Navigate to="/admin/users" replace />;
  if (user.status === "aktiv") return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Iter 19.52 — Liest das öffentliche `registration_disabled`-Flag und rendert
 * entweder die echte Registrierung oder die Coming-Soon-Page. Der Wrapper
 * sitzt in einer Default-Position (`disabled=true` während des Fetches),
 * damit niemand kurz das Register-Formular sieht, falls der Fetch lange dauert.
 */
function RegisterGate() {
  const [flag, setFlag] = React.useState(undefined); // undefined → laden; true → coming soon; false → echte Page
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await import("./lib/api").then((m) =>
          m.api.get("/system/public-flags")
        );
        if (!cancelled) setFlag(!!data?.registration_disabled);
      } catch {
        // Fallback bei Netz-Fehler: sicher = Coming-Soon zeigen
        if (!cancelled) setFlag(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  if (flag === undefined) return <FullScreenLoader />;
  return flag ? <ComingSoonPage /> : <RegisterPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      {/* Iter 19.63.108 — Öffentliche Hilfe-/Doku-Route. Bewusst OHNE
          ProtectedRoute, damit User direkt aus dem Login-Screen oder einer
          E-Mail-Verlinkung darauf zugreifen können. Keine sensiblen Daten. */}
      <Route path="/hilfe" element={<HelpPage />} />
      {/* Preview-Mockup — UI/UX-Audit-Vorschau Schritt 3. Public, keine Auth,
          nur statische Daten, 0 Backend-Calls. */}
      <Route path="/mockup/step3" element={<MockupStep3Page />} />
      {/* Iter 20.08 — Public Demo-Routes. Landing zeigt zwei Case-Karten;
          `/demo/:caseId` rendert den echten Tool-Wizard mit gemocktem
          Backend (kein LLM, kein DB-Write, kein Login nötig). */}
      <Route path="/demo" element={<DemoLandingPage />} />
      <Route path="/demo/:caseId" element={<DemoToolPage />} />
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <RegisterGate />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/passwort-vergessen"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/pending"
        element={
          <PendingRoute>
            <PendingPage />
          </PendingRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireStatus="aktiv">
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil"
        element={
          <ProtectedRoute requireStatus="aktiv">
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* ADMIN AREA */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kvs"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminKvsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute requireRole="superadmin">
            <AdminCompaniesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kvs/:id/struktur"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminKvStructuredEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/statistik"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminStatisticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/systemstatus"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sv-codes"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminSvCodesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-einstellungen"
        element={
          <ProtectedRoute requireRole="superadmin">
            <AdminSystemSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/testsuite"
        element={
          <ProtectedRoute requireRole="superadmin">
            <AdminTestsuitePage />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <EinstufungProgressProvider>
          <AppRoutes />
        </EinstufungProgressProvider>
      </ChatProvider>
    </AuthProvider>
  );
}
